import React, { useContext, useEffect, useState } from "react";

import {
  makeStyles,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import CreateIcon from "@material-ui/icons/Create";
import AddRoundedIcon from "@material-ui/icons/AddRounded";

import ConfirmationModal from "../../../components/ConfirmationModal";
import Button from "../../../components/Button";
import EditIcon from "../../../components/Table/EditIcon";
import DeleteIcon from "../../../components/Table/DeleteIcon";
import ModalDialog from "../../../components/Form/ModalDialog";
import LightPurpleContainer from "../../../components/LightPurpleContainer";
import Table from "../../../components/Table/Table";
import TableHeadItem from "../../../components/Table/TableHeadItem";
import TableInfoItem from "../../../components/Table/TableInfoItem";
import UserForm from "../../../components/Form/UserForm";
import { HospitalsContext } from "../RodaHomepage";
import { User } from "../../../types/hospital";
import { errorCodes } from "../../../constants/errors";
import {
  deactivateUserById,
  getNurseUsersByHospitalId, reactivateUserById,
} from "../../../api/user";
import { blankUser } from "../../../constants/objects";
import ActivateIcon from "../../../components/Table/ActivateIcon";
import { INACTIVE } from "../../../constants/status";
import { handleNurseAction } from "../util/handleNurseAction";

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    fontSize: theme.spacing(2.2),
    color: theme.palette.primary.main,
  },
  title: {
    color: theme.palette.primary.main,
    display: "inline-block",
    fontSize: theme.spacing(2.5),
    fontWeight: "bold",
    margin: theme.spacing(1.8),
  },
  marginTop: {
    marginTop: theme.spacing(3),
  },
  inactive: {
    color: theme.palette.grey["600"]
  }
}));

export default function NursesTable() {
  const classes = useStyles();
  const [nurseId, setNurseId] = useState<number>();
  const [nurses, setNurses] = useState<User[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState<boolean>(false);
  const [confirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [editNurse, setEditNurse] = useState<User>(blankUser);
  const [triggerUseEffect, setTriggerUseEffect] = useState<boolean>(false);
  const { currentHospital, setError } = useContext(HospitalsContext);

  async function getNurses() {
    try {
      const response = await getNurseUsersByHospitalId(currentHospital);
      setNurses(response.data.userDTOList);
    } catch (error: any) {
      if (error.response?.status === errorCodes.NOT_FOUND) {
        setError(true);
      }
    }
  }

  useEffect(() => {
    getNurses();
  }, [currentHospital, setError, triggerUseEffect]);

  function deleteNurse(nurseId: number) {
    handleNurseAction(nurseId, deactivateUserById, setNurses, setDeleteModalOpen, setNurseId, getNurses);
  }

  function reactivateNurse(nurseId: number) {
    handleNurseAction(nurseId, reactivateUserById, setNurses, setReactivateModalOpen, setNurseId, getNurses);
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="button"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditNurse(blankUser);
            setModalOpen(true);
          }}
        >
          Dodaj
        </Button>
        {modalOpen && (
          <ModalDialog
            modalOpen={modalOpen}
            handleClose={() => setModalOpen(false)}
            maxWidth="md"
            title={
              <Typography className={classes.title}>
                Dodaj zdravstvenog djelatnika
              </Typography>
            }
            icon={<AddRoundedIcon className={classes.editIcon} />}
          >
            <UserForm
              setTriggerUseEffect={() => setTriggerUseEffect(!triggerUseEffect)}
              userUpdate={blankUser}
              hospitalId={currentHospital}
              handleClose={() => {
                setModalOpen(false);
              }}
              handleConfirm={() => {
                setModalOpen(false);
                setConfirmationModalOpen(true);
              }}
            />
          </ModalDialog>
        )}
        {confirmationModalOpen && (
          <ConfirmationModal
            modalOpen={confirmationModalOpen}
            handleClose={() => {
              setConfirmationModalOpen(false);
            }}
            modalText={
              "Zdravstvenom djelatniku je poslan e-mail s linkom za postavljanje lozinke."
            }
            title={"Link je na putu!"}
          />
        )}
      </div>
      <LightPurpleContainer className={classes.marginTop}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadItem>Ime i prezime</TableHeadItem>
              <TableHeadItem>Email</TableHeadItem>
              <TableHeadItem>Akcija</TableHeadItem>
            </TableRow>
          </TableHead>
          <TableBody>
            {nurses.map((nurse) => (
              <TableRow key={nurse.name}>
                <TableInfoItem className={nurse.status === INACTIVE ? classes.inactive : ""}>
                  {nurse.name + " " + nurse.surname}
                </TableInfoItem>
                <TableInfoItem className={nurse.status === INACTIVE ? classes.inactive : ""}>{nurse.email}</TableInfoItem>
                <TableInfoItem>
                  {nurse.status !== INACTIVE && (
                    <>
                  <EditIcon
                    onClick={() => {
                      setEditNurse(nurse);
                      setEditModalOpen(true);
                    }}
                  />
                  <DeleteIcon
                    onClick={() => {
                      if (nurse.id) {
                        setNurseId(nurse.id);
                      }
                      setDeleteModalOpen(true);
                    }}
                  />
                    </>)}
                  {nurse.status === INACTIVE && (
                    <ActivateIcon
                      onClick={() => {
                        if (nurse.id) {
                          setNurseId(nurse.id);
                        }
                        setReactivateModalOpen(true);
                      }}
                    />
                  )}
                </TableInfoItem>
              </TableRow>
            ))}
            {reactivateModalOpen && (
              <ConfirmationModal
                modalOpen={reactivateModalOpen}
                handleAction={() => reactivateNurse(nurseId!)}
                handleClose={() => setReactivateModalOpen(false)}
                modalText={
                  "Jeste li sigurni da želite ponovno aktivirati korisnički račun zdravstvenog djelatnika?"
                }
                title={"Jeste li sigurni?"}
              />
            )}
            {deleteModalOpen && (
              <ConfirmationModal
                modalOpen={deleteModalOpen}
                handleAction={() => deleteNurse(nurseId!)}
                handleClose={() => setDeleteModalOpen(false)}
                modalText={
                  "Jeste li sigurni da želite deaktivirati korisnički račun zdravstvenog djelatnika?"
                }
                title={"Jeste li sigurni?"}
              />
            )}
            {editModalOpen && (
              <ModalDialog
                modalOpen={editModalOpen}
                handleClose={() => setEditModalOpen(false)}
                maxWidth="md"
                title={
                  <Typography className={classes.title}>
                    Uredi informacije
                  </Typography>
                }
                icon={<CreateIcon className={classes.editIcon} />}
              >
                <UserForm
                  setTriggerUseEffect={() =>
                    setTriggerUseEffect(!triggerUseEffect)
                  }
                  userUpdate={editNurse}
                  hospitalId={currentHospital}
                  handleClose={() => setEditModalOpen(false)}
                />
              </ModalDialog>
            )}
          </TableBody>
        </Table>
      </LightPurpleContainer>
    </>
  );
}
